import type React from 'react';
import { toast, type ToastOptions } from 'react-toastify';
import './AosNoticeToast.css';

export type AosNoticeToastType = 'success' | 'error' | 'warning' | 'info';

export interface AosNoticeToastProps {
  type?: AosNoticeToastType;
  message?: React.ReactNode;
  description?: string;
  onClose?: () => void;
}

export interface ShowAosNoticeToastOptions {
  type?: AosNoticeToastType;
  message: React.ReactNode;
  description?: string;
  toastId?: string;
  autoClose?: number;
  position?: ToastOptions['position'];
}

function StatusIcon({ type }: { type: AosNoticeToastType }): React.ReactElement {
  if (type === 'warning') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" />
      </svg>
    );
  }

  if (type === 'success') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-.997-6.167 7.07-7.071-1.414-1.414-5.657 5.657-2.829-2.829-1.414 1.414 4.243 4.243z" />
      </svg>
    );
  }

  if (type === 'info') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-11v6h2v-6h-2zm0-4v2h2V7h-2z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" />
    </svg>
  );
}

export function AosNoticeToast({
  type = 'info',
  message,
  description,
  onClose,
}: AosNoticeToastProps): React.ReactElement {
  const hasMessage = message != null && message !== '';
  const hasDescription = Boolean(description?.trim());

  return (
    <div
      className={`aos-notice-toast aos-notice-toast--${type}`}
      role="alert"
    >
      <span className="aos-notice-toast__icon">
        <StatusIcon type={type} />
      </span>
      <div className="aos-notice-toast__body">
        {hasMessage ? <p className="aos-notice-toast__message">{message}</p> : null}
        {hasDescription ? (
          <p className="aos-notice-toast__description">{description}</p>
        ) : null}
      </div>
      {onClose ? (
        <button
          type="button"
          className="aos-notice-toast__close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
      ) : null}
    </div>
  );
}

export function showAosNoticeToast({
  type = 'error',
  message,
  description,
  toastId,
  autoClose = 5000,
  position,
}: ShowAosNoticeToastOptions): void {
  toast(
    ({ closeToast }) => (
      <AosNoticeToast
        type={type}
        message={message}
        description={description}
        onClose={closeToast}
      />
    ),
    {
      toastId,
      autoClose,
      position,
      icon: false,
      closeButton: false,
      hideProgressBar: true,
      className: 'aos-notice-toast-shell',
    }
  );
}
